import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tree } from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ExplainedButton as Button, explainMenuItem } from '../components/explained-button';
import { Menu } from 'primereact/menu';
import { useCompactViewport } from '../general/use-compact-viewport';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { useApi } from '../providers/api-provider';
import { ProtectedStorageEntryDto as Entry } from '../general/dtos';
import { ContentPage } from '../components/content-page';
import { UserPromptDialog } from '../components/dialogs/prompt-dialog';
import { GeneralInfoDialog } from '../components/dialogs/info-dialog';
import { GeneralErrorDialog } from '../components/dialogs/error-dialog';
import '../assets/protected-storage.css';
import { useBookmarkSelection, useBookmarkValues } from '../general/bookmark-selection';

type Prompt = { kind: 'new' | 'rename' | 'move' | 'delete'; targets: Entry[] };
type Upload = { name: string; percent: number; status: 'queued' | 'uploading' | 'saved' | 'failed'; error?: string };
const parentPath = (path: string) => path.split('/').slice(0, -1).join('/');
const joinPath = (parent: string, name: string) => parent ? parent + '/' + name : name;
const key = (path: string) => 'folder:' + path;
const errorText = (error: any): string => error?.response?.data?.message || error?.message || 'Please try again.';
const escapeHtml = (text: string) => text.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]!));
const fileSize = (size: number | null) => {
    if (size === null) return '—';
    if (size < 1024) return size + ' B';
    if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
    return (size / (1024 * 1024)).toFixed(1) + ' MB';
};

export default function AdminProtectedStoragePage() {
    const compact = useCompactViewport();
    const actionsMenu = useRef<Menu>(null);
    const api = useApi();
    const [bookmarkedPath, setBookmarkedPath] = useBookmarkSelection('folder');
    const path = bookmarkedPath ?? '';
    const setPath = (folder: string) => setBookmarkedPath(folder || null);
    const [selected, setSelectedState] = useState<Entry[]>([]);
    const [selectedPaths, setSelectedPaths] = useBookmarkValues('entry');
    const setSelected = (entries: Entry[]) => {
        setSelectedState(entries);
        setSelectedPaths(entries.map(entry => entry.path));
    };
    const cache = useRef(new Map<string, Entry[]>());
    const pending = useRef(new Map<string, Promise<Entry[] | null>>());
    const controller = useRef(new AbortController());
    const alive = useRef(true);
    const busyRef = useRef(false);
    const [revision, rerender] = useState(0);
    const [loading, setLoading] = useState<Set<string>>(new Set());
    const [loadErrors, setLoadErrors] = useState<Map<string, string>>(new Map());
    const [expanded, setExpanded] = useState<Record<string, boolean>>({ [key('')]: true });
    const [busy, setBusy] = useState('');
    const [prompt, setPrompt] = useState<Prompt | null>(null);
    const [name, setName] = useState('');
    const [destination, setDestination] = useState('');
    const [recursive, setRecursive] = useState(false);
    const [validation, setValidation] = useState('');
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [notice, setNotice] = useState('');
    const [uploads, setUploads] = useState<Upload[]>([]);
    const uploadInput = useRef<HTMLInputElement>(null);
    const downloads = useRef(new Map<string, ReturnType<typeof setTimeout>>());

    const load = useCallback((folder: string): Promise<Entry[] | null> => {
        if (!api || !alive.current) return Promise.resolve(null);
        if (cache.current.has(folder)) return Promise.resolve(cache.current.get(folder)!);
        if (pending.current.has(folder)) return pending.current.get(folder)!;
        const signal = controller.current.signal;
        setLoading(current => new Set(current).add(folder));
        setLoadErrors(current => { const next = new Map(current); next.delete(folder); return next; });
        const request = api.listProtectedStorage(folder, undefined, signal).then(result => {
            if (signal.aborted || !alive.current) return null;
            cache.current.set(folder, result.entries);
            rerender(current => current + 1);
            return result.entries;
        }).catch(failure => {
            if (!signal.aborted && alive.current) setLoadErrors(current => new Map(current).set(folder, errorText(failure)));
            return null;
        }).finally(() => {
            if (!signal.aborted && alive.current) {
                pending.current.delete(folder);
                setLoading(current => { const next = new Set(current); next.delete(folder); return next; });
            }
        });
        pending.current.set(folder, request);
        return request;
    }, [api]);

    useEffect(() => {
        alive.current = true;
        controller.current = new AbortController();
        cache.current.clear(); pending.current.clear();
        void load('');
        return () => {
            alive.current = false; controller.current.abort();
            downloads.current.forEach((timer, url) => { clearTimeout(timer); URL.revokeObjectURL(url); });
            downloads.current.clear();
        };
    }, [load]);

    useEffect(() => {
        const entries = cache.current.get(path) ?? [];
        setSelectedState(entries.filter(entry => selectedPaths.includes(entry.path)));
    }, [path, selectedPaths, revision]);

    useEffect(() => {
        setNotice('');
        const ancestry = ['', ...path.split('/').filter(Boolean).map((_, index, parts) => parts.slice(0, index + 1).join('/'))];
        setExpanded(current => ({ ...current, ...Object.fromEntries(ancestry.map(folder => [key(folder), true])) }));
        ancestry.forEach(folder => { void load(folder); });
    }, [path, load]);

    const navigate = (folder: string) => {
        if (busyRef.current) return;
        setPath(folder); setSelected([]); setNotice('');
        setExpanded(current => ({ ...current, [key(folder)]: true }));
        void load(folder);
    };
    const refresh = async (folder = path) => {
        if (!alive.current) return;
        controller.current.abort(); controller.current = new AbortController();
        cache.current.clear(); pending.current.clear();
        setLoading(new Set()); setLoadErrors(new Map()); setSelected([]);
        const ancestry = ['', ...folder.split('/').filter(Boolean).map((_, index, parts) => parts.slice(0, index + 1).join('/'))];
        let previous = '';
        for (const current of ancestry) {
            if (!alive.current) return;
            if (current && !cache.current.get(previous)?.some(item => item.path === current && item.type === 'folder')) break;
            if (!await load(current)) break;
            previous = current;
        }
        if (alive.current) {
            setPath(previous);
            setExpanded(Object.fromEntries(ancestry.filter(item => cache.current.has(item)).map(item => [key(item), true])));
        }
    };
    const run = async (label: string, action: () => Promise<void>) => {
        if (!api || busyRef.current) return;
        busyRef.current = true; setBusy(label); setValidation(''); setNotice('');
        try { await action(); }
        catch (failure) { if (alive.current) setError(errorText(failure)); }
        finally { busyRef.current = false; if (alive.current) setBusy(''); }
    };

    const currentFolder: Entry[] = path ? [{ name: path.split('/').pop()!, path, type: 'folder', size: null, modified_at: '' }] : [];
    const targets = selected.length ? selected : currentFolder;
    const openPrompt = (kind: Prompt['kind']) => {
        setPrompt({ kind, targets: [...targets] }); setName(kind === 'rename' ? targets[0].name : '');
        setDestination(''); setRecursive(false); setValidation('');
    };
    const submit = () => {
        if (!api || !prompt || busyRef.current) return;
        const names = ['.', '..'];
        if ((prompt.kind === 'new' || prompt.kind === 'rename') && (!name.trim() || names.includes(name)
            || /[\\/\x00-\x1f:*?"<>|]/.test(name) || /[. ]$/.test(name))) {
            setValidation('Enter a valid name without path separators or a trailing dot or space.'); return;
        }
        if (prompt.kind === 'move' && prompt.targets.some(item => item.type === 'folder'
            && (destination === item.path || destination.startsWith(item.path + '/')))) {
            setValidation('A folder cannot be moved into itself or one of its subfolders.'); return;
        }
        void run('Working…', async () => {
            let nextPath = path;
            if (prompt.kind === 'new') {
                await api.createProtectedStorageFolder(joinPath(path, name));
                if (alive.current) setNotice('Folder created.');
            } else if (prompt.kind === 'rename') {
                const item = prompt.targets[0]; const renamed = joinPath(parentPath(item.path), name);
                if (renamed === item.path) { if (alive.current) setPrompt(null); return; }
                await (item.type === 'folder' ? api.renameProtectedStorageFolder(item.path, renamed) : api.renameProtectedStorageFile(item.path, renamed));
                if (item.path === path) nextPath = renamed;
                if (alive.current) setNotice('Item renamed.');
            } else {
                const failures: string[] = [];
                for (const item of prompt.targets) {
                    try {
                        if (prompt.kind === 'delete') {
                            await (item.type === 'folder' ? api.deleteProtectedStorageFolder(item.path, recursive) : api.deleteProtectedStorageFile(item.path));
                            if (item.path === path) nextPath = parentPath(path);
                        } else {
                            const moved = joinPath(destination, item.name);
                            if (moved === item.path) continue;
                            await (item.type === 'folder' ? api.renameProtectedStorageFolder(item.path, moved) : api.renameProtectedStorageFile(item.path, moved));
                            if (item.path === path) nextPath = moved;
                        }
                    } catch (failure) { failures.push(item.name + ': ' + errorText(failure)); }
                }
                if (alive.current) {
                    if (failures.length) setError(failures.join('\n'));
                    else setNotice(prompt.kind === 'delete' ? 'Items deleted.' : 'Items moved.');
                }
            }
            if (alive.current) setPrompt(null);
            await refresh(nextPath);
        });
    };
    const upload = (files: File[]) => void run('Uploading…', async () => {
        if (!api) return;
        const folder = path;
        setUploads(files.map(file => ({ name: file.name, percent: 0, status: 'queued' })));
        const update = (index: number, changes: Partial<Upload>) => {
            if (alive.current) setUploads(current => current.map((item, position) => position === index ? { ...item, ...changes } : item));
        };
        for (let index = 0; index < files.length && alive.current; index++) {
            update(index, { status: 'uploading' });
            try {
                await api.uploadProtectedStorageFile(files[index], folder, undefined, {
                    signal: controller.current.signal, onProgress: percent => update(index, { percent }),
                });
                update(index, { percent: 100, status: 'saved' });
            } catch (failure) { update(index, { status: 'failed', error: errorText(failure) }); }
        }
        await refresh(folder);
    });
    const download = (files: Entry[]) => void run('Downloading…', async () => {
        if (!api) return;
        const failures: string[] = [];
        for (const file of files) {
            try {
                const result = await api.downloadProtectedStorageFile(file.path, controller.current.signal);
                if (!alive.current) return;
                const url = URL.createObjectURL(result.blob); const anchor = document.createElement('a');
                anchor.href = url; anchor.download = file.name; document.body.appendChild(anchor);
                try { anchor.click(); } finally { anchor.remove(); }
                downloads.current.set(url, setTimeout(() => { URL.revokeObjectURL(url); downloads.current.delete(url); }, 1000));
            } catch (failure) { failures.push(file.name + ': ' + errorText(failure)); }
        }
        if (failures.length && alive.current) setError(failures.join('\n'));
    });
    const copyLink = () => void run('Copying link…', async () => {
        if (!api) return;
        const url = api.getProtectedStorageDownloadUrl(selected[0].path);
        try { await navigator.clipboard.writeText(url); if (alive.current) setNotice('Download link copied.'); }
        catch { if (alive.current) setInfo('Copy this download link:<br><code>' + escapeHtml(url) + '</code>'); }
    });

    const nodes = (folder: string, moving = false): TreeNode[] => (cache.current.get(folder) ?? []).filter(item => item.type === 'folder').map(item => ({
        key: key(item.path), label: item.name, icon: loading.has(item.path) ? 'pi pi-spinner pi-spin' : 'pi pi-folder',
        selectable: !moving || !prompt?.targets.some(target => target.type === 'folder'
            && (item.path === target.path || item.path.startsWith(target.path + '/'))),
        leaf: cache.current.has(item.path) && !cache.current.get(item.path)!.some(child => child.type === 'folder'),
        children: cache.current.has(item.path) ? nodes(item.path, moving) : undefined,
    }));
    const tree = (moving = false, showContents?: () => void) => <Tree aria-label={moving ? 'Destination folders' : 'Storage folders'}
        value={[{ key: key(''), label: 'Protected storage', icon: 'pi pi-folder', children: nodes('', moving) }]}
        selectionMode="single" selectionKeys={key(moving ? destination : path)} expandedKeys={expanded}
        onToggle={event => setExpanded(event.value)} onExpand={event => { if (!busyRef.current) void load(String(event.node.key).slice(7)); }}
        onNodeClick={event => {
            if (!moving && !busyRef.current && event.node.key === key(path)) showContents?.();
        }}
        onSelectionChange={event => {
            if (busyRef.current || typeof event.value !== 'string') return;
            const folder = event.value.slice(7);
            if (moving) { setDestination(folder); setValidation(''); void load(folder); }
            else { navigate(folder); showContents?.(); }
        }} />;
    const retry = (folder: string) => { cache.current.delete(folder); void load(folder); };
    const list = cache.current.get(path) ?? [];
    const disabled = !!busy || !api;
    const busyReason = busy ? 'Wait for the storage operation to finish.' : 'Storage is not ready yet. Try again in a moment.';
    const storageActions = [
        { label: 'New folder', icon: 'pi pi-folder-plus', disabled: disabled || !cache.current.has(path), disabledReason: disabled ? busyReason : 'Wait for this folder to finish loading.', command: () => openPrompt('new') },
        { label: 'Upload files', icon: 'pi pi-upload', disabled: disabled || !cache.current.has(path), disabledReason: disabled ? busyReason : 'Wait for this folder to finish loading.', command: () => uploadInput.current?.click() },
        { label: 'Rename', icon: 'pi pi-pencil', disabled: disabled || targets.length !== 1, disabledReason: disabled ? busyReason : 'Select exactly one file or folder to rename.', command: () => openPrompt('rename') },
        { label: 'Move', icon: 'pi pi-folder-open', disabled: disabled || !targets.length, disabledReason: disabled ? busyReason : 'Select files or folders first.', command: () => openPrompt('move') },
        { label: 'Delete', icon: 'pi pi-trash', disabled: disabled || !targets.length, disabledReason: disabled ? busyReason : 'Select files or folders first.', command: () => openPrompt('delete'), severity: 'danger' as const, className: 'storage-action-delete' },
        { label: 'Download', icon: 'pi pi-download', disabled: disabled || !selected.length || selected.some(item => item.type !== 'file'), disabledReason: disabled ? busyReason : 'Select one or more files. Folders cannot be downloaded.', command: () => download(selected) },
        { label: 'Copy link', icon: 'pi pi-link', disabled: disabled || selected.length !== 1 || selected[0].type !== 'file', disabledReason: disabled ? busyReason : 'Select exactly one file to copy its link.', command: copyLink },
        { label: 'Refresh', icon: 'pi pi-refresh', disabled, disabledReason: busyReason, command: () => void run('Refreshing…', () => refresh()) },
    ];
    const explainedActions = storageActions.map(action => explainMenuItem(action, action.disabledReason));
    const toolbar = <div className="storage-toolbar admin-toolbar" role="toolbar" aria-label="Storage operations">
        {compact ? <>
            <Button label="Actions" icon="pi pi-chevron-down" text size="small" disabled={disabled}
                aria-haspopup="menu" aria-controls="storage-actions-menu" onClick={event => actionsMenu.current?.toggle(event)} />
            <Menu id="storage-actions-menu" popup ref={actionsMenu} model={explainedActions} className="storage-actions-menu" />
        </> : storageActions.map(action => <Button key={action.label} label={action.label} icon={action.icon}
            text size="small" severity={action.severity} disabled={action.disabled} disabledReason={action.disabledReason} onClick={action.command} />)}
    </div>;
    const crumbs = ['', ...path.split('/').filter(Boolean).map((_, index, parts) => parts.slice(0, index + 1).join('/'))];
    return <div className="protected-storage-page">
        <ContentPage pageId="protected-storage" pageTitle="Protected storage" leftPanelTitle="Folders" rightPanelTitle="Folder contents" leftContentPercent={25}
            mobileTabs initialMobilePanel="right" mobileBackLabel="Back to folders"
            mobileRightPanelTitle="Contents"
            leftContent={showContents => <div className="storage-tree">{tree(false, showContents)}
                {loading.has('') && <p role="status">Loading folders…</p>}
                {[...loadErrors].map(([folder, message]) => <div key={folder}><p role="alert">{folder || 'Protected storage'}: {message}</p>
                    <Button label="Retry" text size="small" disabled={disabled} onClick={() => retry(folder)} /></div>)}
            </div>}
            rightContent={<div className="storage-content">{toolbar}
                <input ref={uploadInput} type="file" multiple hidden aria-label="Files to upload" onChange={event => {
                    const files = Array.from(event.target.files ?? []); event.target.value = ''; if (files.length) upload(files);
                }} />
                <nav aria-label="Folder path" className="storage-breadcrumbs">{crumbs.map((folder, index) => <React.Fragment key={folder}>
                    {index > 0 && <span aria-hidden="true">/</span>}<Button label={folder ? folder.split('/').pop() : 'Protected storage'} text size="small"
                        disabled={disabled} onClick={() => navigate(folder)} /></React.Fragment>)}</nav>
                <div role="status">{busy || notice || (selected.length ? selected.length + ' selected' : 'Current folder: ' + (path || 'Protected storage'))}</div>
                <div className="storage-table"><DataTable value={list} dataKey="path" selection={selected} selectionMode="multiple" metaKeySelection={false}
                    onSelectionChange={event => { if (!busyRef.current) setSelected(event.value); }} loading={loading.has(path)}
                    scrollable scrollHeight="flex" sortField="name" sortOrder={1} emptyMessage={loadErrors.has(path) ? 'Folder could not be loaded. Use Retry or Refresh.' : 'This folder is empty.'}
                    onRowDoubleClick={event => { if (!busyRef.current && event.data.type === 'folder') navigate(event.data.path); }}>
                    <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                    <Column field="name" header="Name" sortable body={(item: Entry) => <span><i aria-hidden="true" className={'mr-2 pi ' + (item.type === 'folder' ? 'pi-folder' : 'pi-file')} />
                        {item.type === 'folder' ? <button className="storage-folder-link" disabled={disabled} onClick={event => { event.stopPropagation(); navigate(item.path); }}>{item.name}</button> : item.name}</span>} />
                    <Column field="size" header="Size" sortable body={(item: Entry) => fileSize(item.size)} />
                    <Column field="modified_at" header="Last modified" sortable body={(item: Entry) => new Date(item.modified_at).toLocaleString()} />
                </DataTable></div>
                {uploads.length > 0 && <div className="storage-uploads" aria-label="Upload results">{uploads.map((item, index) => <div key={index}>
                    <span>{item.name} — {item.status === 'uploading' ? item.percent + '%' : item.status}</span>
                    {item.status === 'uploading' && <progress value={item.percent} max={100} aria-label={'Uploading ' + item.name} />}
                    {item.error && <span role="alert">: {item.error}</span>}
                </div>)}</div>}
            </div>} />
        <UserPromptDialog visible={!!prompt} title={prompt ? ({ new: 'New folder', rename: 'Rename item', move: 'Move items', delete: 'Delete items' }[prompt.kind]) : ''}
            question={prompt?.kind === 'delete' ? 'Delete the selected items? This action cannot be undone.' : prompt?.kind === 'move' ? 'Select the destination folder.' : 'Enter a name.'}
            onClose={() => { if (!busyRef.current) setPrompt(null); }} actions={[
                <Button label="Cancel" text disabled={disabled} disabledReason={busyReason} onClick={() => setPrompt(null)} />,
                <Button label={prompt?.kind === 'delete' ? 'Delete' : prompt?.kind === 'move' ? 'Move' : 'Save'} severity={prompt?.kind === 'delete' ? 'danger' : undefined}
                    disabled={disabled || (prompt?.kind === 'move' && !cache.current.has(destination))} disabledReason={disabled ? busyReason : 'Wait for the destination folder to finish loading.'} onClick={submit} />,
            ]}>
            {prompt && (prompt.kind === 'new' || prompt.kind === 'rename') && <><label htmlFor="storage-name">Name</label>
                <InputText id="storage-name" autoFocus value={name} disabled={disabled} onChange={event => { setName(event.target.value); setValidation(''); }}
                    onKeyDown={event => { if (event.key === 'Enter') submit(); }} /></>}
            {prompt && prompt.kind !== 'new' && <ul>{prompt.targets.map(item => <li key={item.path}>{item.path}</li>)}</ul>}
            {prompt && prompt.kind !== 'new' && <small>Download links use file paths. Renaming, moving, or deleting items can break links already inserted into module content.</small>}
            {prompt?.kind === 'move' && <div className="storage-move-tree">{tree(true)}</div>}
            {prompt?.kind === 'move' && [...loadErrors].map(([folder, message]) => <div key={folder}>
                <p role="alert">{folder || 'Protected storage'}: {message}</p>
                <Button label="Retry" text disabled={disabled} onClick={() => retry(folder)} />
            </div>)}
            {prompt?.kind === 'delete' && prompt.targets.some(item => item.type === 'folder') && <div className="flex align-items-center gap-2">
                <Checkbox inputId="storage-recursive" checked={recursive} disabled={disabled} onChange={event => setRecursive(!!event.checked)} />
                <label htmlFor="storage-recursive">Also delete all contents of selected folders</label></div>}
            {validation && <p role="alert">{validation}</p>}
        </UserPromptDialog>
        <GeneralErrorDialog visible={!!error} title="Protected storage" message="The operation could not be completed." details={error} onClose={() => setError('')} />
        <GeneralInfoDialog visible={!!info} title="Download link" message={info} onClose={() => setInfo('')} />
    </div>;
}
